import { alpha, Box, Container, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { useMediaUp } from 'hooks';
import { FC } from 'react';
import { PlusIcon, RedCirclePointedIcon } from 'svgs';

export interface ContactUsBlockProps {
  image: string;
  title: string;
  text: string;
  upperTitle: string;
  titleBg: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(14, 0, 0),
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(14, 0),
    },
  },
  redCircleIcon: {
    position: 'absolute',
    top: theme.spacing(10),
    right: theme.spacing(-8.4),
    [theme.breakpoints.up('sm')]: {
      top: theme.spacing(16),
    },
    [theme.breakpoints.up('md')]: {
      right: theme.spacing(5),
    },
    [theme.breakpoints.up('lg')]: {
      right: theme.spacing(10),
    },
  },
  plusIcon1: {
    position: 'absolute',
    bottom: theme.spacing(69),
    right: theme.spacing(4),
    [theme.breakpoints.up('sm')]: {
      right: theme.spacing(45),
      bottom: theme.spacing(101),
    },
    [theme.breakpoints.up('md')]: {
      right: theme.spacing(32),
      bottom: theme.spacing(22),
    },
    [theme.breakpoints.up('lg')]: {
      right: theme.spacing(75),
    },
  },
  plusIcon2: {
    position: 'absolute',
    top: theme.spacing(8),
    left: theme.spacing(10),
    [theme.breakpoints.up('sm')]: {
      top: theme.spacing(8),
      left: theme.spacing(23),
    },
    [theme.breakpoints.up('md')]: {
      left: theme.spacing(114),
    },
    [theme.breakpoints.up('lg')]: {
      left: theme.spacing(190),
    },
  },
  bg: {
    backgroundColor: theme.palette.primary.dark,
    minHeight: 451,
    width: '95%',
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: -1,
    borderBottomLeftRadius: 50,
    [theme.breakpoints.up('md')]: {
      width: '65%',
      height: '100%',
    },
  },
  titleBg: {
    color: alpha(theme.palette.common.white, 0.05),
    fontSize: 50,
    position: 'absolute',
    bottom: theme.spacing(15),
    fontWeight: 700,
    zIndex: -1,
    whiteSpace: 'nowrap',
    [theme.breakpoints.up('sm')]: {
      fontSize: 90,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 104,
      bottom: theme.spacing(-6),
      right: theme.spacing(-7),
    },
    [theme.breakpoints.up('lg')]: {
      fontSize: 165,
      bottom: theme.spacing(-12),
      left: theme.spacing(7),
    },
  },
  firstBlock: {
    justifyContent: 'flex-end',
    marginTop: theme.spacing(17),
    display: 'flex',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(23),
      justifyContent: 'center',
    },
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(10),
    },
  },
  title: {
    color: theme.palette.switchedColors.prompter,
    writingMode: 'vertical-rl',
    transform: 'rotateZ(180deg)',
    userSelect: 'none',
    fontSize: 38,
    marginRight: 0,
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(3),
      fontSize: 62,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 85,
    },
    [theme.breakpoints.up('lg')]: {
      marginRight: theme.spacing(4),
    },
  },
  image: {
    borderRadius: theme.spacing(0, 3, 3, 0),
    boxShadow: 'none',
    maxWidth: 272,
    maxHeight: 302,
    [theme.breakpoints.up('sm')]: {
      boxShadow: `0px 4px 23px ${alpha(theme.palette.common.black, 0.25)}`,
      maxWidth: 406,
      maxHeight: 451,
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: 406,
      maxHeight: 451,
    },
  },
  upperTitle: {
    color: theme.palette.common.white,
    marginTop: theme.spacing(8),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(22),
    },
  },
  text: {
    maxWidth: 270,
    marginTop: theme.spacing(3),
    color: theme.palette.common.white,
    [theme.breakpoints.up('sm')]: {
      maxWidth: 405,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 24,
    },
  },
  secondBlock: {
    marginLeft: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(14),
    },
    [theme.breakpoints.up('lg')]: {
      marginLeft: theme.spacing(6),
    },
  },
}));

const ContactUsBlock: FC<ContactUsBlockProps> = (props) => {
  const { title, text, image, upperTitle, titleBg } = props;
  const classes = useStyles();
  const isMediumUp = useMediaUp('md');

  return (
    <Box className={classes.root}>
      <Box className={classes.bg}>
        <Typography className={classes.titleBg}>{titleBg}</Typography>
      </Box>
      <RedCirclePointedIcon className={classes.redCircleIcon} />
      <PlusIcon className={classes.plusIcon1} />
      <PlusIcon className={classes.plusIcon2} />
      <Grid justifyContent={'center'} container>
        <Grid sm={12} lg={10} item>
          <Container maxWidth={false} fixed>
            <Grid direction={isMediumUp ? 'row' : 'column-reverse'} container>
              <Grid xs={12} md={6} className={classes.firstBlock} item>
                <Typography className={classes.title} variant="h1">
                  {title}
                </Typography>
                <img src={image} alt={image} className={classes.image} />
              </Grid>
              <Grid xs={12} md={5} className={classes.secondBlock} item>
                <Grid container justifyContent="flex-end">
                  <Grid item>
                    <Typography variant="h1" className={classes.upperTitle}>
                      {upperTitle}
                    </Typography>
                    <Typography variant="subtitle2" className={classes.text}>
                      {text}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactUsBlock;
